import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Users, Trash2, MessageSquare,
  MapPin, Target, Loader2, Plus
} from "lucide-react";
import { SignalProgress } from "@/components/ui/signal-progress";
import { StatusBadge } from "@/components/ui/status-badge";
import { usePartnerContext } from "@/hooks/usePartnerContext";
import { useShortlists } from "@/hooks/useShortlists";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getSkillById } from "@/lib/skills";
import { format } from "date-fns";
import { toast } from "sonner";

export default function EmpresaShortlistDetalhe() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { orgId } = usePartnerContext();
  const { shortlists, isLoading: shortlistsLoading, removeItem } = useShortlists(orgId);

  const shortlist = shortlists.find((s) => s.id === id);
  const itemUserIds = ((shortlist?.shortlist_items ?? []) as any[]).map((i: any) => i.user_id as string);

  // Fetch profiles for all talent IDs in shortlist
  const { data: talentProfiles, isLoading: profilesLoading } = useQuery({
    queryKey: ["shortlist_profiles", id, itemUserIds],
    enabled: itemUserIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, city, state, objective, profile_status")
        .in("id", itemUserIds);
      if (error) throw error;
      return data ?? [];
    },
  });

  // Fetch skills for all talents
  const { data: allSkills } = useQuery({
    queryKey: ["shortlist_skills", itemUserIds],
    enabled: itemUserIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("student_skills" as any)
        .select("user_id, skill_id")
        .in("user_id", itemUserIds);
      if (error) throw error;
      return data as any[];
    },
  });

  // Fetch career signals for all talents
  const { data: allSignals } = useQuery({
    queryKey: ["shortlist_signals", itemUserIds],
    enabled: itemUserIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profile_career_signals")
        .select("user_id, status")
        .in("user_id", itemUserIds);
      if (error) throw error;
      return data ?? [];
    },
  });

  const getSkillsForTalent = (userId: string) =>
    (allSkills ?? []).filter((s: any) => s.user_id === userId).map((s: any) => s.skill_id as string).slice(0, 3);

  const getSignalCount = (userId: string) =>
    (allSignals ?? []).filter((s: any) => s.user_id === userId && s.status === "done").length;

  const handleRemove = (userId: string) => {
    if (!id) return;
    removeItem.mutate({ shortlistId: id, userId }, {
      onSuccess: () => toast.success("Talento removido da shortlist"),
      onError: () => toast.error("Erro ao remover"),
    });
  };

  const isLoading = shortlistsLoading || profilesLoading;

  if (isLoading) {
    return <div className="page-container-desktop flex items-center justify-center min-h-[50vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  if (!shortlist) {
    return (
      <div className="page-container-desktop text-center py-20">
        <p className="text-muted-foreground">Shortlist não encontrada.</p>
        <Button variant="ghost" className="mt-4" onClick={() => navigate("/empresa/shortlists")}>
          <ArrowLeft className="w-4 h-4 mr-2" />Voltar
        </Button>
      </div>
    );
  }

  const talents = talentProfiles ?? [];

  return (
    <div className="page-container-desktop">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/empresa/shortlists")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground">{shortlist.name}</h1>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate("/empresa")}>
          <Plus className="w-4 h-4" />Adicionar Talento
        </Button>
      </motion.div>

      {/* Info Bar */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="flex items-center gap-6 mb-6 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4" />
          <span>{talents.length} talentos</span>
        </div>
        <span>•</span>
        <span>Criada em: {format(new Date(shortlist.created_at), "dd/MM/yyyy")}</span>
      </motion.div>

      {/* Talentos List */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="space-y-3">
        {talents.map((talento) => {
          const skills = getSkillsForTalent(talento.id);
          const signalCount = getSignalCount(talento.id);
          return (
            <div key={talento.id} className="bg-card rounded-2xl p-5 border border-border hover:border-primary/30 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <span className="text-lg font-semibold text-primary">
                      {(talento.full_name ?? "?").charAt(0)}
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-heading font-semibold text-foreground">{talento.full_name ?? "Sem nome"}</h3>
                      <StatusBadge status={(talento.profile_status ?? "construction") as any} />
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                      {(talento.city || talento.state) && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />{[talento.city, talento.state].filter(Boolean).join(", ")}
                        </span>
                      )}
                      {talento.objective && (
                        <span className="flex items-center gap-1">
                          <Target className="w-3 h-3" />{talento.objective}
                        </span>
                      )}
                    </div>
                    {skills.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {skills.map((sid) => (
                          <Badge key={sid} variant="secondary" className="text-xs">{getSkillById(sid)?.label ?? sid}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <SignalProgress current={signalCount} total={6} size="sm" />
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => navigate(`/empresa/talento/${talento.id}`)}>
                      Ver Perfil
                    </Button>
                    <Button variant="accent" size="sm"
                      onClick={() => navigate(`/empresa/contato-mediado?userId=${talento.id}&name=${encodeURIComponent(talento.full_name ?? "")}`)}>
                      <MessageSquare className="w-4 h-4" />Contato
                    </Button>
                    <Button variant="ghost" size="icon-sm" onClick={() => handleRemove(talento.id)}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </motion.div>

      {/* Empty State */}
      {talents.length === 0 && (
        <div className="bg-muted/50 rounded-2xl p-12 text-center">
          <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-heading font-semibold text-foreground mb-2">Nenhum talento nesta shortlist</h3>
          <p className="text-muted-foreground mb-4">Adicione talentos da vitrine para esta shortlist</p>
          <Button variant="accent" onClick={() => navigate("/empresa")}>Ir para Vitrine</Button>
        </div>
      )}
    </div>
  );
}
